import { ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { logTrace } from '../telemetry/logTrace';
import LoadingSpinner from '../widgets/LoadingSpinner';
import { benefitUserManager } from './oidcConfig';
import { verifyAutoconnectSessionToken } from './verifyAutoconnectSessionToken';

export function AutoconnectSession({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [token] = useState(() =>
    new URLSearchParams(window.location.search).get('autoconnect-session'),
  );
  const [isPending, setIsPending] = useState(token !== null);
  const [error, setError] = useState<Error | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (token === null || auth.isLoading || hasStarted.current) {
      return;
    }

    // Keep one handoff across StrictMode's effect replay, even after the URL is cleaned.
    hasStarted.current = true;
    const url = new URL(window.location.href);
    url.searchParams.delete('autoconnect-session');
    window.history.replaceState({}, '', url);

    (async () => {
      try {
        const isTokenValid = await verifyAutoconnectSessionToken(token);
        logTrace('AutoconnectSession: token verified', { isTokenValid });

        if (!isTokenValid) {
          setIsPending(false);
          return;
        }

        // Use UserManager directly so failures propagate instead of being swallowed by useAuth.
        await benefitUserManager.removeUser();
        window.sessionStorage.setItem(
          'redirectTo',
          `${url.pathname}${url.search}`,
        );
        logTrace('AutoconnectSession: starting fresh signin');
        await benefitUserManager.signinRedirect();
      } catch (cause) {
        const failure =
          cause instanceof Error ? cause : new Error(String(cause));
        logTrace('AutoconnectSession: handoff failed', {
          message: failure.message,
        });
        setError(failure);
      }
    })();
  }, [auth.isLoading, token]);

  if (error) {
    throw error;
  }

  return isPending ? <LoadingSpinner size="large" /> : <>{children}</>;
}
